// src/encounter/lib/cps-layout.ts
// AUTHOR-CONTROLLED STEP TITLES + HIDDEN STEPS — the read side of the `cps-layout` contract.
//
// A template author wants to rename the encounter step tabs for their specialty ("Subjective"
// instead of "HPI", "Exam" instead of "PE") and, separately, to remove the four steps that are
// theirs to remove. The carrier is the EXISTING `cps-layout` root extension on the Questionnaire
// (valueString, a JSON document) — NOT a new extension URL: QuestionBank persists only six known
// URLs and drops (or 400s) anything else, so a bespoke URL would never survive a round-trip. PA
// had no reader for `cps-layout` at all; this is it, scoped to the two root keys the contract
// defines:
//
//     { "stepTitles"?: { [K in EncounterStepKey]?: string }, "hiddenSteps"?: string[] }
//
// where `EncounterStepKey` is the DS's closed seven-member union: hpi | risk | ros | pe | docs |
// ap | wrap-up.
//
// FIXED CONTRACT (do not widen without the authoring side moving in lockstep):
//   • ALL SEVEN step keys are title-overridable. Absent key · absent step · empty string ·
//     whitespace-only  →  fall back to the built-in STEP_LABELS value. An authored empty title
//     must never produce a blank tab.
//   • ONLY hpi / risk / ros / pe may be HIDDEN. A/P is order-bearing and physician-only by founder
//     ruling and can never be removed from the nav; any other key found in `hiddenSteps` is
//     stripped silently, never surfaced as an error.
//   • The title value is UNTRUSTED display text: it is trimmed, whitespace-collapsed to one line,
//     and capped; it is rendered as a React text child (never `dangerouslySetInnerHTML`), so markup
//     in it is escaped, not executed.
//
// EVERY defensive branch below returns the EMPTY override (map or array), never throws: a
// malformed layout must degrade to the built-in labels and full step set, not break the encounter.
// Mirrors `parseCpsRule`'s posture (cps-rule.ts) for the sibling extension on the same resource.
import type { EncounterStepKey } from '@scripsteam/scrips-signal-ds';
import { CPS_LAYOUT_EXTENSION_URL, type FhirExtension } from '@/types/template-studio-api';

/** The step keys whose nav title an author may override — ALL SEVEN `EncounterStepKey` members
 *  (the DS's closed union), in the DS's own order. This is the wire contract shared with the
 *  authoring app; it must not silently widen beyond that union. */
export const STEP_TITLE_KEYS = ['hpi', 'risk', 'ros', 'pe', 'docs', 'ap', 'wrap-up'] as const satisfies readonly EncounterStepKey[];
export type StepTitleKey = (typeof STEP_TITLE_KEYS)[number];

/** The step keys a template author may HIDE. Deliberately a strict subset of `STEP_TITLE_KEYS`:
 *  A/P carries orders and is physician-only by founder ruling, and `docs`/`wrap-up` are not
 *  questionnaire-authored steps — none of the three may ever be removed from the nav. */
export const HIDEABLE_STEP_KEYS = ['hpi', 'risk', 'ros', 'pe'] as const satisfies readonly StepTitleKey[];
export type HideableStepKey = (typeof HIDEABLE_STEP_KEYS)[number];

/** Author-supplied step titles, already sanitized. A key is PRESENT only when it survived
 *  sanitization — so a consumer can treat "present" as "safe to render". */
export type StepTitleOverrides = Partial<Record<StepTitleKey, string>>;

/** The parsed `cps-layout` document — only the keys PA reads. Unknown keys are ignored, not
 *  rejected: the layout document is shared with other consumers and will grow. */
export interface CpsLayout {
  stepTitles?: StepTitleOverrides;
  hiddenSteps?: readonly HideableStepKey[];
}

/** Longest rendered step title, in characters. The nav keeps ALL steps on one row (the one-row
 *  ruling — a wrapping nav was reverted in #170), so an author cannot be allowed to push a
 *  paragraph into a tab. Longer titles are truncated with an ellipsis. */
export const MAX_STEP_TITLE_LENGTH = 24;

/** Match the cps-layout extension URL — the short constant exactly, or a full StructureDefinition
 *  URL on a `/` boundary (so `…/cps-layout` matches but an unrelated `…not-cps-layout` does not).
 *  Same rule `pickEncounterQuestionnaire` uses for `cps-rule`.
 *
 *  THE ONE MATCHER (PROD-2129). This is exported because the app had TWO, and they disagreed:
 *  this one accepted both forms, while `fhirToDsQuestionnaire` compared against a hard-coded full
 *  `https://fhir.scrips.com/StructureDefinition/cps-layout` string and nothing else. A template
 *  whose layout arrived under the SHORT url therefore kept its authored step titles and silently
 *  lost its columns — the two halves of one extension read by two rules. Both sites now call
 *  this; do not reintroduce a local comparison. */
export function isCpsLayoutUrl(url: unknown): boolean {
  return typeof url === 'string' && (url === CPS_LAYOUT_EXTENSION_URL || url.endsWith(`/${CPS_LAYOUT_EXTENSION_URL}`));
}

/** Is this extension the `cps-layout` carrier? Takes `unknown` deliberately — a wire extension
 *  array is untrusted, so a null / non-object / url-less member must answer `false` rather than
 *  throw inside the `.find` that both readers run it in. */
export function isCpsLayoutExtension(ext: unknown): boolean {
  return typeof ext === 'object' && ext !== null && isCpsLayoutUrl((ext as { url?: unknown }).url);
}

/**
 * Sanitize ONE authored title. Returns `undefined` when the value cannot be rendered as a title —
 * the caller then falls back to the built-in label. Rules, in order:
 *   1. not a string (number / object / null / undefined) → undefined
 *   2. control characters (incl. newlines/tabs) are collapsed to spaces, runs of whitespace to one
 *   3. trimmed
 *   4. empty after trim (so `''` and `'   '` and `'\n\t'` all land here) → undefined
 *   5. longer than MAX_STEP_TITLE_LENGTH → truncated with a trailing ellipsis
 * No HTML is stripped or interpreted: the result is rendered as a text node, where React escapes it.
 */
export function sanitizeStepTitle(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  // Control chars are matched deliberately (that is the point of the sanitizer).
  // eslint-disable-next-line no-control-regex
  const oneLine = value.replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (oneLine === '') return undefined;
  if (oneLine.length <= MAX_STEP_TITLE_LENGTH) return oneLine;
  return `${oneLine.slice(0, MAX_STEP_TITLE_LENGTH - 1).trimEnd()}…`;
}

/** The sanitized override map for a raw `stepTitles` value of unknown shape. Non-object (incl.
 *  `null` and arrays), unknown keys, and values that fail `sanitizeStepTitle` are dropped. */
function toStepTitles(raw: unknown): StepTitleOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const source = raw as Record<string, unknown>;
  const out: StepTitleOverrides = {};
  for (const key of STEP_TITLE_KEYS) {
    const title = sanitizeStepTitle(source[key]);
    if (title !== undefined) out[key] = title;
  }
  return out;
}

/** The sanitized hidden-step set for a raw `hiddenSteps` value of unknown shape. Non-array
 *  (including `null`/object/string), non-string members, and any key outside `HIDEABLE_STEP_KEYS`
 *  (notably `ap` — never removable, per founder ruling) are dropped silently. Duplicates collapse
 *  because the result is built through a `Set`. */
function toHiddenSteps(raw: unknown): readonly HideableStepKey[] {
  if (!Array.isArray(raw)) return [];
  const hideable = new Set<string>(HIDEABLE_STEP_KEYS);
  const out = new Set<HideableStepKey>();
  for (const entry of raw) {
    if (typeof entry === 'string' && hideable.has(entry)) out.add(entry as HideableStepKey);
  }
  return [...out];
}

/**
 * Parse a `cps-layout` extension `valueString` into the layout document. `null` when absent, empty,
 * not valid JSON, or not a JSON object (a bare string/number/array/`null` document) — every one of
 * those is an authoring mistake, and every one degrades to the built-in labels and full step set.
 */
export function parseCpsLayout(valueString: string | null | undefined): CpsLayout | null {
  if (typeof valueString !== 'string' || valueString.trim() === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(valueString);
  } catch {
    return null; // malformed JSON → no layout
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const doc = parsed as Record<string, unknown>;
  const layout: CpsLayout = {};
  if ('stepTitles' in doc) layout.stepTitles = toStepTitles(doc.stepTitles);
  if ('hiddenSteps' in doc) layout.hiddenSteps = toHiddenSteps(doc.hiddenSteps);
  return layout;
}

/**
 * The author's step-title overrides for a questionnaire — `{}` whenever anything at all is off
 * (no questionnaire, no/!array extension list, no cps-layout entry, absent/malformed valueString,
 * wrong `stepTitles` type, unusable individual titles). NEVER throws.
 */
export function stepTitlesOf(
  questionnaire: { extension?: FhirExtension[] | null } | null | undefined,
): StepTitleOverrides {
  const extensions = questionnaire?.extension;
  if (!Array.isArray(extensions)) return {};
  const entry = extensions.find((e) => isCpsLayoutExtension(e));
  if (!entry) return {};
  return parseCpsLayout(entry.valueString)?.stepTitles ?? {};
}

/**
 * The author's hidden-step set for a questionnaire — ADDITIVE and empty (`[]`) whenever anything
 * at all is off (no questionnaire, no/!array extension list, no cps-layout entry,
 * absent/malformed valueString, wrong `hiddenSteps` type, unhideable/unknown members). NEVER
 * throws. An empty result always leaves the full seven-step catalog in play — hiding is opt-in,
 * never the default.
 */
export function hiddenStepsOf(
  questionnaire: { extension?: FhirExtension[] | null } | null | undefined,
): readonly HideableStepKey[] {
  const extensions = questionnaire?.extension;
  if (!Array.isArray(extensions)) return [];
  const entry = extensions.find((e) => isCpsLayoutExtension(e));
  if (!entry) return [];
  return parseCpsLayout(entry.valueString)?.hiddenSteps ?? [];
}
